import React, { useMemo } from 'react';

import {
    getDefaultCanEditLine,
    getDefaultCanReceiveLine,
    isFcTransferItemReceivedZero,
} from 'components/warehouse/components/fcTransfers/acceptTransfer/acceptTransferLineCapabilities';
import { AcceptTransferLineRow } from 'components/warehouse/components/fcTransfers/acceptTransfer/AcceptTransferLineRow';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';

/**
 * Column headers for the accept-transfer line items grid (tech spec §3.2).
 * Exported for unit tests that assert stable labels / ordering.
 */
export const ACCEPT_TRANSFER_LINE_ITEMS_COLUMN_HEADERS = Object.freeze([
    { id: 'sourceLot', label: 'Source Lot' },
    { id: 'newLot', label: 'New Lot' },
    { id: 'sku', label: 'SKU' },
    { id: 'product', label: 'Product' },
    { id: 'expected', label: 'Expected' },
    { id: 'received', label: 'Received' },
    { id: 'expectedBbd', label: 'Expected BBD' },
    { id: 'receivedBbd', label: 'Received BBD' },
    { id: 'expectedUnitsPerCase', label: 'Expected count / case' },
    { id: 'receivedUnitsPerCase', label: 'Received count / case' },
    { id: 'status', label: 'Status' },
    { id: 'actions', label: 'Actions' },
]);

/**
 * Receive-page line items table; Receive/Edit opens the parent-controlled receive modal.
 *
 * @param {Object} props
 * @param {Array<Record<string, unknown>>} [props.items=[]]
 * @param {(item: Record<string, unknown>) => void} [props.onOpenReceiveModal]
 * @param {string | number | null} [props.savePendingItemId=null]
 */
export function AcceptTransferLineItemsTable({ items = [], onOpenReceiveModal, savePendingItemId = null }) {
    const rows = useMemo(() => (Array.isArray(items) ? items : []), [items]);

    return (
        <div className="rounded-md border bg-card p-6 shadow-sm">
            <div className="flex flex-col gap-4">
                <h2 className="text-left text-lg font-semibold tracking-tight text-foreground">Line items</h2>
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                {ACCEPT_TRANSFER_LINE_ITEMS_COLUMN_HEADERS.map((col) => (
                                    <TableHead key={col.id} className="whitespace-nowrap text-left">
                                        {col.label}
                                    </TableHead>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.length ? (
                                rows.map((item, index) => {
                                    const itemPk = item?.id ?? item?.transferItemId ?? `idx-${index}`;
                                    const idKey = item?.id ?? item?.transferItemId;

                                    const receive =
                                        item?.canReceive !== undefined && item?.canReceive !== null
                                            ? Boolean(item.canReceive)
                                            : getDefaultCanReceiveLine(item);
                                    const editCap =
                                        item?.canEdit !== undefined && item?.canEdit !== null
                                            ? Boolean(item.canEdit)
                                            : getDefaultCanEditLine(item);

                                    const showReceive = Boolean(receive);
                                    const showEdit = Boolean(editCap);

                                    const receivedZero = isFcTransferItemReceivedZero(item);
                                    const printPallet = !receivedZero && Boolean(item?.canPrintPalletLabels);
                                    const printCase = !receivedZero && Boolean(item?.canPrintCaseLabels);

                                    const pending =
                                        savePendingItemId !== null &&
                                        savePendingItemId !== undefined &&
                                        idKey !== undefined &&
                                        String(savePendingItemId) === String(idKey);

                                    return (
                                        <AcceptTransferLineRow
                                            key={String(itemPk)}
                                            actionsDisabled={pending}
                                            item={item}
                                            showEdit={showEdit}
                                            showPrintCase={printCase}
                                            showPrintPallet={printPallet}
                                            showReceive={showReceive}
                                            onEdit={() => onOpenReceiveModal?.(item)}
                                            onReceive={() => onOpenReceiveModal?.(item)}
                                        />
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell
                                        colSpan={ACCEPT_TRANSFER_LINE_ITEMS_COLUMN_HEADERS.length}
                                        className="h-24 text-center text-muted-foreground"
                                    >
                                        No line items on this transfer.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </div>
    );
}

AcceptTransferLineItemsTable.displayName = 'AcceptTransferLineItemsTable';
